import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Trash2, Edit, CheckCircle, ArrowRight } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BulkActionsBar = ({ 
  selectedItems, 
  onClearSelection, 
  actions,
  type = 'items' 
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');

  const handleAction = (actionKey) => {
    const action = actions.find(a => a.key === actionKey);
    if (action) {
      if (action.requiresConfirm) {
        setSelectedAction(actionKey);
        setShowDeleteConfirm(true);
      } else {
        action.handler(selectedItems);
      }
    }
  };

  const confirmAction = () => {
    const action = actions.find(a => a.key === selectedAction);
    if (action) {
      action.handler(selectedItems);
    }
    setShowDeleteConfirm(false);
    setSelectedAction('');
  };

  if (selectedItems.length === 0) return null;

  return (
    <>
      <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40 animate-slideUp">
        <div className="bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl p-4 flex items-center gap-4">
          {/* Selection Count */}
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-primary text-white border-primary">
              {selectedItems.length} {type} ausgewählt
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClearSelection}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="h-8 w-px bg-zinc-700" />

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {actions.map((action) => {
              if (action.type === 'select') {
                return (
                  <Select key={action.key} onValueChange={action.handler}>
                    <SelectTrigger className="w-[180px] h-9 bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder={action.label} />
                    </SelectTrigger>
                    <SelectContent>
                      {action.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }

              const Icon = action.icon || Edit;
              const variant = action.variant || 'outline';

              return (
                <Button
                  key={action.key}
                  variant={variant}
                  size="sm"
                  onClick={() => handleAction(action.key)}
                  className="h-9"
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {action.label}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aktion bestätigen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diese Aktion für {selectedItems.length} {type} ausführen? 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAction} className="bg-red-600 hover:bg-red-700">
              Bestätigen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default BulkActionsBar;
